<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { ACCEPTED_FILES } from "@/utils/uploadDocument";
import { MAX_DOCUMENTS } from "@/store/modules/context";
import { readClipboard } from "@/utils/pasteContext";
import { driveAvailable, pickFromDrive } from "@/utils/googleDrive";

// Everything the genie can read instead of being told: pasted messages and screenshots,
// files, web pages, Google Drive and the user's kept documents. Pasting or dropping
// anywhere on the tab works too.
const store = useStore();

const documents = computed(() => store.state.context.documents);
const images = computed(() => store.state.context.images);
const uploading = computed(() => store.state.context.uploading);
const uploadError = computed(() => store.state.context.uploadError);
const loggedIn = computed(() => store.state.auth.loggedIn);
const isMobile = computed(() => store.state.isMobile);
const savedDocuments = computed(() => store.state.context.savedDocuments);
const canAttach = computed(
  () => documents.value.length + uploading.value < MAX_DOCUMENTS
);
const hasItems = computed(
  () => documents.value.length || images.value.length || uploading.value
);

const loadSavedDocuments = () => store.dispatch("context/loadSavedDocuments");
onMounted(loadSavedDocuments);
watch(loggedIn, (value) =>
  value ? loadSavedDocuments() : store.commit("context/SET_SAVED_DOCUMENTS", [])
);

const isAttached = (id) => documents.value.some((doc) => doc.savedId === id);
// A few recent kept documents, one tap to add again
const recent = computed(() =>
  savedDocuments.value.filter((saved) => !isAttached(saved.id)).slice(0, 3)
);

const note = ref("");
const say = (message) => {
  note.value = message;
  setTimeout(() => (note.value = ""), 4000);
};

// Paste
const shortcut = computed(() =>
  /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘V" : "Ctrl+V"
);
const paste = async () => {
  const pasted = await readClipboard();
  if (!pasted) {
    say(
      isMobile.value
        ? "Your browser didn't allow it. Press and hold in the notes box to paste instead."
        : `Your browser didn't allow it. Press ${shortcut.value} anywhere here instead.`
    );
    return;
  }
  if (!pasted.files.length && !pasted.text.trim()) {
    say(
      "There's nothing to paste yet. Copy an email, chat or screenshot first."
    );
    return;
  }
  store.dispatch("context/addPasted", pasted);
};

// Upload
const fileInput = ref(null);
const chooseFiles = () => canAttach.value && fileInput.value?.click();
const onFilesChosen = (event) => {
  store.dispatch("context/uploadDocuments", [...event.target.files]);
  event.target.value = "";
};

// Google Drive
const drivePicking = ref(false);
const fromDrive = async () => {
  drivePicking.value = true;
  try {
    const files = await pickFromDrive();
    if (files.length) store.dispatch("context/uploadDocuments", files);
  } catch (error) {
    say(error.message || "Google Drive didn't open, please try again");
  }
  drivePicking.value = false;
};

// One panel at a time: a web address, or the whole document library
const panel = ref("");
const linkInput = ref(null);
const togglePanel = async (name) => {
  panel.value = panel.value === name ? "" : name;
  if (panel.value === "link") {
    await nextTick();
    linkInput.value?.focus();
  }
};
const link = ref("");
const addLink = async () => {
  if (await store.dispatch("context/addLink", link.value)) {
    link.value = "";
    panel.value = "";
  }
};

const attachSaved = (id) => store.dispatch("context/attachSavedDocument", id);
const deleteSaved = (id) => store.dispatch("context/deleteSavedDocument", id);
const saveDocument = (index) => store.dispatch("context/saveDocument", index);
const removeDocument = (index) =>
  store.dispatch("context/removeDocument", index);
const removeImage = (index) => store.dispatch("context/removeImage", index);

const describe = (doc) => {
  const kind = doc.url
    ? "Web page"
    : /^Pasted - /.test(doc.name)
    ? "Pasted"
    : "Document";
  const words = Math.max(1, Math.round((doc.characters || 0) / 6));
  const size =
    words >= 1000
      ? `about ${Math.round(words / 1000)}k words`
      : `about ${words} words`;
  return `${kind} · ${size}${doc.truncated ? " · only the start is used" : ""}`;
};
// Pasted text is named after how it starts, so it's shown as that opening
const displayName = (doc) =>
  /^Pasted - /.test(doc.name)
    ? `${doc.name.replace(/^Pasted - /, "").replace(/\.txt$/, "")}…`
    : doc.name;

const tiles = computed(() => [
  { id: "paste", icon: "paste", label: "Paste", action: paste },
  {
    id: "upload",
    icon: "arrow-up-from-bracket",
    label: "Upload",
    action: chooseFiles,
    disabled: !canAttach.value,
  },
  {
    id: "link",
    icon: "link",
    label: "Web page",
    action: () => togglePanel("link"),
    pressed: panel.value === "link",
  },
  ...(driveAvailable
    ? [
        {
          id: "drive",
          icon: ["fab", "google-drive"],
          label: drivePicking.value ? "Opening..." : "Google Drive",
          action: fromDrive,
          disabled: !canAttach.value || drivePicking.value,
        },
      ]
    : []),
  ...(loggedIn.value
    ? [
        {
          id: "library",
          icon: "folder-open",
          label: savedDocuments.value.length
            ? `My documents (${savedDocuments.value.length})`
            : "My documents",
          action: () => togglePanel("library"),
          pressed: panel.value === "library",
        },
      ]
    : []),
]);
</script>

<template>
  <section id="shepherd-target-documents">
    <div
      class="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-10/60 p-3 sm:p-4"
    >
      <div
        class="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap"
        role="group"
        aria-label="Add something to read"
      >
        <button
          v-for="tile in tiles"
          :key="tile.id"
          type="button"
          class="flex items-center justify-center gap-2 rounded-xl border bg-white px-3 py-2.5 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:justify-start sm:py-2.5"
          :class="
            tile.pressed
              ? 'border-accentPrimary text-accentPrimary'
              : 'border-gray-200 text-primary hover:border-primary'
          "
          :aria-expanded="tile.pressed === undefined ? undefined : tile.pressed"
          :disabled="tile.disabled"
          @click="tile.action"
        >
          <font-awesome-icon
            :icon="Array.isArray(tile.icon) ? tile.icon : ['fas', tile.icon]"
            class="text-accentPrimary"
          />
          {{ tile.label }}
        </button>
      </div>
      <p class="mt-3 text-xs text-secondary">
        Like an email thread to reply to, a chat, a job advert or last year's
        version.
        <span class="hidden lg:inline"
          >Paste ({{ shortcut }}) or drop files anywhere here.</span
        >
        PDF, Word, text or photos up to 10MB.
      </p>

      <form
        v-if="panel === 'link'"
        class="mt-3 flex gap-2"
        @submit.prevent="addLink"
      >
        <label for="context-link" class="sr-only">Web page address</label>
        <input
          id="context-link"
          ref="linkInput"
          v-model="link"
          type="text"
          inputmode="url"
          autocomplete="url"
          enterkeyhint="go"
          placeholder="Paste a web address"
          class="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-base text-primary outline-none focus:border-accentPrimary sm:text-sm"
        />
        <button
          type="submit"
          class="shrink-0 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:bg-secondaryLight"
          :disabled="!canAttach || !link.trim()"
        >
          Add page
        </button>
      </form>

      <div
        v-if="loggedIn && panel === 'library'"
        class="mt-3 rounded-xl bg-white p-3 text-primary"
      >
        <p v-if="!savedDocuments.length" class="text-sm text-secondary">
          Nothing kept yet. Press Keep on anything you add to use it again in
          future makes.
        </p>
        <ul v-else class="divide-y divide-gray-100">
          <li
            v-for="saved in savedDocuments"
            :key="saved.id"
            class="flex items-center justify-between gap-3 py-2"
          >
            <span class="min-w-0 truncate text-sm font-bold">{{
              saved.name
            }}</span>
            <span class="flex shrink-0 items-center gap-3">
              <button
                type="button"
                class="text-sm font-bold text-accentPrimary hover:text-purple-400 disabled:text-secondary"
                :disabled="isAttached(saved.id) || !canAttach"
                @click="attachSaved(saved.id)"
              >
                {{ isAttached(saved.id) ? "Added" : "Add" }}
              </button>
              <button
                type="button"
                :aria-label="`Delete ${saved.name} from My documents`"
                :title="`Delete ${saved.name} from My documents`"
                class="-m-2 p-2 text-secondary hover:text-red-600"
                @click="deleteSaved(saved.id)"
              >
                <font-awesome-icon :icon="['fas', 'trash-can']" />
              </button>
            </span>
          </li>
        </ul>
      </div>

      <div
        v-if="loggedIn && recent.length && panel !== 'library'"
        class="mt-3 flex flex-wrap items-center gap-2"
      >
        <span class="text-xs font-bold text-secondary">Recent:</span>
        <button
          v-for="saved in recent"
          :key="saved.id"
          type="button"
          class="max-w-[14rem] truncate rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-primary transition-colors hover:border-accentPrimary hover:text-accentPrimary disabled:opacity-50"
          :disabled="!canAttach"
          :title="`Add ${saved.name}`"
          @click="attachSaved(saved.id)"
        >
          + {{ saved.name }}
        </button>
      </div>

      <input
        ref="fileInput"
        type="file"
        class="hidden"
        multiple
        :accept="ACCEPTED_FILES"
        @change="onFilesChosen"
      />
    </div>

    <p v-if="note" class="mt-2 text-sm text-secondary" role="status">
      {{ note }}
    </p>
    <p v-if="uploadError" class="mt-2 text-sm font-medium text-red-500">
      {{ uploadError }}
    </p>

    <div v-if="hasItems" class="mt-3 space-y-2">
      <ul v-if="images.length" class="flex flex-wrap gap-3">
        <li
          v-for="(image, index) in images"
          :key="image.name + index"
          class="relative"
        >
          <img
            :src="image.dataUrl"
            :alt="image.name"
            :title="image.name"
            class="h-20 w-20 rounded-xl object-cover shadow-sm"
          />
          <button
            type="button"
            :aria-label="`Remove ${image.name}`"
            class="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-secondary shadow hover:text-red-600"
            @click="removeImage(index)"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </li>
      </ul>
      <ul class="space-y-2">
        <li
          v-for="(doc, index) in documents"
          :key="doc.name + index"
          class="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-primary"
        >
          <font-awesome-icon
            :icon="[
              'fas',
              doc.url
                ? 'globe'
                : /^Pasted - /.test(doc.name)
                ? 'paste'
                : 'file-lines',
            ]"
            class="shrink-0 text-lg text-accentPrimary"
          />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-bold" :title="doc.name">
              {{ displayName(doc) }}
            </p>
            <p class="text-xs text-secondary">{{ describe(doc) }}</p>
          </div>
          <button
            v-if="loggedIn"
            type="button"
            class="flex shrink-0 items-center text-xs font-bold"
            :class="
              doc.savedId
                ? 'cursor-default text-secondary'
                : 'text-accentPrimary hover:text-purple-400'
            "
            :disabled="Boolean(doc.savedId)"
            :title="
              doc.savedId
                ? 'Kept in My documents'
                : 'Keep this in My documents for future makes'
            "
            @click="saveDocument(index)"
          >
            <font-awesome-icon :icon="['fas', 'bookmark']" class="mr-1" />
            {{ doc.savedId ? "Kept" : "Keep" }}
          </button>
          <button
            type="button"
            :aria-label="`Remove ${doc.name}`"
            class="-m-1 shrink-0 p-1 text-secondary hover:text-red-600"
            @click="removeDocument(index)"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </li>
        <li
          v-for="n in uploading"
          :key="'uploading' + n"
          class="animate-pulse rounded-xl bg-gray-10 px-3 py-3 text-sm text-secondary"
        >
          Reading...
        </li>
      </ul>
    </div>
  </section>
</template>
